// Toggle to see Sidebar with the blocked times of users
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ExoAvatar from '../ExoAvatar';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AppBlockingIcon from '@mui/icons-material/AppBlocking';
import { useState } from 'react';

export const BlockedUserTimes = ({
  openBlockedTimes, //boolean
  blockedUserTimesData,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();

  const [expandedUser, setExpandedUser] = useState(null);

  return (
    <>
      {openBlockedTimes && (
        <Box
          sx={{
            bgcolor: colors.glass,
            backdropFilter: 'blur(10px)',
            width: '400px',
            height: '100%',
            borderRadius: 2,
            p: 2,
            overflowY: 'auto',
          }}
        >
          <Typography fontWeight={600} className="pb-2">
            {t('Blocked Times of Users')}
          </Typography>
          {blockedUserTimesData.map((user) => {
            const isExpanded = expandedUser === user.id;

            return (
              <Box
                key={user.id}
                className="flex flex-col w-full p-2 mb-2 rounded-lg"
                sx={{
                  backgroundColor: colors.card,
                  cursor: 'default',
                  border: `1px solid transparent`,
                  ':hover': {
                    border: `1px solid ${colors.info}`,
                  },
                }}
              >
                {/* Header: Avatar + Name + Dropdown toggle */}
                <Box
                  className="flex items-start justify-between gap-2 cursor-pointer"
                  onClick={() => setExpandedUser(isExpanded ? null : user.id)}
                >
                  <Box className="flex items-start gap-2">
                    {/* Avatar */}
                    <ExoAvatar
                      picture={
                        user.picture?.temporaryUrl ||
                        user.picture?.thumbnailUrl ||
                        ''
                      }
                      name={`${user.firstName} ${user.lastName}`}
                      icon={user?.icon}
                      size="35"
                    />

                    {/* Name + Position */}
                    <Box>
                      <Typography fontSize="14px" fontWeight={600}>
                        {user.firstName} {user.lastName}
                      </Typography>
                      <Typography
                        fontSize="12px"
                        sx={{ color: colors.primary[600] }}
                      >
                        {user.position || 'No Position'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Toggle Label */}
                  <Typography
                    fontSize="12px"
                    sx={{ color: colors.info[500], fontWeight: 500, mt: 0.5 }}
                  >
                    <ArrowDropDownIcon
                      sx={{
                        transform: `rotate(${isExpanded ? 180 : 0}deg)`,
                      }}
                      className="mr-1"
                    />
                  </Typography>
                </Box>

                {/* Dropdown with times */}
                {isExpanded && (
                  <Box className="mt-2 px-1 flex flex-col">
                    <Box className="flex flex-col w-full gap-1 py-1">
                      {[...user.blockedTimes]
                        .sort(
                          (a, b) => (b.wholeDay ? 1 : 0) - (a.wholeDay ? 1 : 0)
                        )
                        .map((time, idx) => {
                          const isWholeDay = time.wholeDay;
                          const start = moment(time.startTime);
                          const end = moment(time.endTime);

                          const formattedStart = isWholeDay
                            ? start.format('YYYY-MM-DD')
                            : start.format('YYYY-MM-DD HH:mm');

                          const formattedEnd = isWholeDay
                            ? end.format('YYYY-MM-DD')
                            : end.format('YYYY-MM-DD HH:mm');

                          return (
                            <Box
                              key={idx}
                              sx={{
                                bgcolor: colors.bgInput,
                                borderRadius: '8px',
                                border: `2px solid ${colors.error}80`,
                                px: 1,
                                py: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <AppBlockingIcon
                                sx={{
                                  fontSize: '20px',
                                  color: colors.error[500],
                                }}
                              />

                              <Typography
                                fontSize="12px"
                                sx={{ wordBreak: 'break-word' }}
                              >
                                {formattedStart} → {formattedEnd} (
                                {isWholeDay ? t('Whole Day') : t('Partial')})
                              </Typography>
                            </Box>
                          );
                        })}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
};
